import type { CSSProperties } from 'react'

import { cn } from '../lib/utils'

/**
 * The blurred white streak that sweeps across the placeholder — a 30px white
 * bar with a 25px blur, per the RP-5936 handoff prototype (visually the
 * Custom Made configurator shimmer). It only exists under `motion-safe`, so
 * `prefers-reduced-motion` gets a static gray placeholder with no shimmer.
 */
const SHIMMER_CLASSES =
  "motion-safe:after:content-[''] motion-safe:after:absolute motion-safe:after:top-0 motion-safe:after:left-0 motion-safe:after:h-full motion-safe:after:w-[30px] motion-safe:after:bg-white motion-safe:after:blur-[25px] motion-safe:after:animate-skeleton-shimmer"

export interface SkeletonProps {
  /** Fixed width — a number is treated as px. Omit to size via `className`. */
  w?: number | string
  /** Fixed height — a number is treated as px. Omit to size via `className`. */
  h?: number | string
  /** Border radius — a number is treated as px. Defaults to 2px (the
   * `rounded-sm` token, the prototype's bar radius). */
  r?: number | string
  /** CSS `aspect-ratio` (e.g. `'5/6'`) — pairs with a fluid width. */
  ar?: string
  className?: string
}

function toCssSize(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value
}

/**
 * Loading placeholder block (RP-5936), local to this package on purpose —
 * promoting it to `@susu/ui` for org-wide use is a deliberate later decision.
 *
 * Geometry comes from `w`/`h`/`r`/`ar` (inline styles, so any value works
 * without Tailwind arbitrary-class generation) or from `className` when the
 * size is responsive (e.g. `w-[90px] md:w-[130px]`).
 */
export default function Skeleton({
  w,
  h,
  r = 2,
  ar,
  className,
}: SkeletonProps) {
  const style: CSSProperties = { borderRadius: toCssSize(r) }

  if (w !== undefined) style.width = toCssSize(w)
  if (h !== undefined) style.height = toCssSize(h)
  if (ar !== undefined) style.aspectRatio = ar

  return (
    <div
      aria-hidden="true"
      data-testid="skeleton"
      className={cn(
        'relative overflow-hidden bg-gray-300',
        SHIMMER_CLASSES,
        className,
      )}
      style={style}
    />
  )
}
